import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAppraisal, type AppraisalDetail } from '../lib/appraisal';
import { summarizeJobSync, type JobSyncSummary } from '../lib/jobStatus';
import { getMapsLink } from '../lib/navigate';
import { getItemsByJob } from '../lib/syncQueue';

function addressOf(a: AppraisalDetail): string {
  if (a.property_address) return a.property_address;
  const parts = [a.address_street, a.address_city, a.address_state, a.address_zip].filter(Boolean);
  return parts.length ? parts.join(', ') : '';
}

export function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AppraisalDetail | null>(null);
  const [sync, setSync] = useState<JobSyncSummary | null>(null);
  const [photoCount, setPhotoCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [showReinspect, setShowReinspect] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const [a, s, items] = await Promise.all([
          getAppraisal(jobId),
          summarizeJobSync(jobId),
          getItemsByJob(jobId),
        ]);
        if (cancelled) return;
        setData(a);
        setSync(s);
        setPhotoCount(items.length);
        setFlaggedCount(
          items.filter((i) => {
            const di = i.metadata.device_info as { flagged?: boolean } | undefined;
            return di?.flagged === true;
          }).length,
        );
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (!jobId) return <div className="p-4 text-sm text-red-600">Missing job ID.</div>;
  if (err) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <Link to="/" className="text-sm text-gray-600 underline">‹ Jobs</Link>
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Error loading job: {err}
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-4 text-sm text-gray-500">Loading…</div>;

  const address = addressOf(data);
  const adjuster =
    data.contacts?.find((c) => (c.role ?? '').toLowerCase() === 'adjuster')?.name ?? null;
  const scheduledInspection =
    data.inspections?.slice().sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : Infinity;
      const db = b.date ? new Date(b.date).getTime() : Infinity;
      return da - db;
    })[0] ?? null;
  const isCompleted = ['completed', 'closed', 'archived'].includes(
    (data.status ?? '').toLowerCase(),
  );
  const hasLocalPhotos = photoCount > 0;

  let ctaLabel: string;
  let ctaDisabled = false;
  let ctaAction: () => void;
  if (isCompleted) {
    ctaLabel = 'Reinspect';
    ctaAction = () => setShowReinspect(true);
  } else if (hasLocalPhotos) {
    ctaLabel = sync?.status === 'synced' ? 'Continue Inspection' : 'Resume Inspection';
    ctaAction = () => navigate(`/job/${jobId}/camera?mode=new`);
  } else {
    ctaLabel = 'Start Inspection';
    ctaAction = () => navigate(`/job/${jobId}/camera?mode=new`);
  }

  function handleNavigate() {
    if (!address) return;
    window.location.href = getMapsLink(address);
  }

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to="/" className="text-sm text-gray-600 underline">
        ‹ Jobs
      </Link>

      {/* Header */}
      <div className="mt-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold">
            {data.policyholder_name || '(no name)'}
          </h1>
          {data.claim_number && (
            <div className="text-xs text-gray-500">Claim {data.claim_number}</div>
          )}
        </div>
        {data.status && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase text-gray-600">
            {data.status}
          </span>
        )}
      </div>

      {/* Address card */}
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
          Address
        </div>
        <div className="mt-1 text-sm">{address || '(no address)'}</div>
        <button
          onClick={handleNavigate}
          disabled={!address}
          className="mt-2 rounded-lg bg-eva-teal px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
        >
          Navigate
        </button>
      </div>

      {/* Info grid */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <InfoTile label="Carrier" value={data.carrier_name} />
        <InfoTile label="Adjuster" value={adjuster} />
        <InfoTile label="Policy #" value={data.policy_number} />
        <InfoTile
          label="Scheduled"
          value={
            scheduledInspection?.date
              ? new Date(scheduledInspection.date).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : null
          }
        />
      </div>

      {/* Highlighted loss info */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-mine/20 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-mine">
            Type of Loss
          </div>
          <div className="mt-0.5 font-semibold">{data.loss_type || '—'}</div>
        </div>
        <div className="rounded-lg bg-bracket-amber/20 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-bracket-amber">
            Date of Loss
          </div>
          <div className="mt-0.5 font-semibold">
            {data.loss_date ? new Date(data.loss_date).toLocaleDateString() : '—'}
          </div>
        </div>
      </div>

      {/* Appointment note */}
      {scheduledInspection?.notes && (
        <div className="mt-3 rounded-lg border border-bracket-amber/40 bg-bracket-amber/10 p-3 text-sm">
          <div className="text-[10px] font-bold uppercase tracking-wide text-bracket-amber">
            Appointment note
          </div>
          <div className="mt-0.5">{scheduledInspection.notes}</div>
        </div>
      )}

      {/* Quick access */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Link
          to={`/job/${jobId}/documents`}
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm"
        >
          <span className="font-semibold">Documents</span>
          <span className="text-gray-400">›</span>
        </Link>
        <Link
          to={`/job/${jobId}/photos`}
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm"
        >
          <div>
            <div className="font-semibold">Photos</div>
            {photoCount > 0 && (
              <div className="text-xs text-gray-500">
                {photoCount}
                {flaggedCount > 0 && (
                  <span className="ml-1 text-red-500">· {flaggedCount} flagged</span>
                )}
              </div>
            )}
          </div>
          <span className="text-gray-400">›</span>
        </Link>
      </div>

      <Link
        to={`/job/${jobId}/notepad`}
        className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm"
      >
        <span className="font-semibold">Notepad</span>
        <span className="text-gray-400">›</span>
      </Link>

      {/* Primary CTA */}
      <button
        onClick={ctaAction}
        disabled={ctaDisabled}
        className="mt-6 w-full rounded-lg bg-eva-teal px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
      >
        {ctaLabel}
      </button>

      {/* Reinspection modal */}
      {showReinspect && (
        <ReinspectionModal
          jobId={jobId}
          onClose={() => setShowReinspect(false)}
        />
      )}
    </div>
  );
}

function InfoTile({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-0.5 font-semibold">{value || '—'}</div>
    </div>
  );
}

function ReinspectionModal({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [choice, setChoice] = useState<'new_set' | 'add_to_existing' | null>(null);
  function confirm() {
    if (!choice) return;
    navigate(`/job/${jobId}/camera?mode=reinspection&rtype=${choice}`);
  }
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl bg-white p-4 sm:rounded-2xl"
      >
        <h2 className="text-lg font-bold">Reinspect this job</h2>
        <p className="mt-1 text-sm text-gray-600">
          This job is already complete. How should the new session number its photos?
        </p>
        <div className="mt-4 space-y-2">
          <button
            onClick={() => setChoice('new_set')}
            className={`w-full rounded-lg border p-3 text-left ${
              choice === 'new_set'
                ? 'border-eva-teal bg-eva-teal/10'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold">New Photo Set</div>
            <div className="text-xs text-gray-600">
              Fresh session; sequence starts at #1.
            </div>
          </button>
          <button
            onClick={() => setChoice('add_to_existing')}
            className={`w-full rounded-lg border p-3 text-left ${
              choice === 'add_to_existing'
                ? 'border-eva-teal bg-eva-teal/10'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold">Add to Existing</div>
            <div className="text-xs text-gray-600">
              Continues from the last sequence + 1.
            </div>
          </button>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-2 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!choice}
            className="flex-1 rounded-lg bg-eva-teal py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
